/**
 * <blog-post-toc>
 * Progressive enhancement for the modular blog post table of contents.
 * - Mobile: toggles the collapsible nav open/closed.
 * - Desktop: highlights the active section link while scrolling (scroll-spy).
 * - Both: nests each section's sub-headings under its entry, collapsed by default.
 * Sticky positioning and desktop/mobile visibility are handled in CSS.
 */
class BlogPostToc extends HTMLElement {
  connectedCallback() {
    // Guard against re-binding listeners if the element is re-connected (e.g. moved in the theme editor).
    if (this._initialized) return;
    this._initialized = true;

    this.toggle = this.querySelector('[data-toc-toggle]');

    // Build the nested entries first so the sub-links pick up the shared link handling below.
    this.buildSubnavs();

    this.links = Array.from(this.querySelectorAll('[data-toc-link]'));

    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        const open = this.classList.toggle('is-open');
        this.toggle.setAttribute('aria-expanded', String(open));
      });
    }

    // Collapse the mobile nav after choosing a destination.
    this.links.forEach((link) => {
      link.addEventListener('click', () => {
        this.classList.remove('is-open');
        if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      });
    });

    this.applyInitialHash();
    this.observeSections();
  }

  /**
   * Sub-headings live inside the section rich text, which is merchant-authored: a rich_text_field
   * stores headings as `{"type":"heading","level":N}` and the level is whatever was picked in the
   * admin editor. So match any heading level rather than assuming h3 — everything inside the scan
   * scope is a sub-heading by construction, because the section's own <h2> sits outside it.
   */
  buildSubnavs() {
    this.querySelectorAll('[data-toc-item]').forEach((item) => {
      // A theme-editor re-render can run this over markup that already has a sublist.
      if (item.querySelector('.blog-post__toc-sublist')) return;

      const sectionId = item.dataset.tocItem;
      const section = document.getElementById(sectionId);
      // Scoped to [data-toc-scan] on purpose: scanning the whole section would pull in
      // product-card titles from the in-article carousel.
      const scope = section?.querySelector('[data-toc-scan]');
      if (!scope) return;

      const headings = Array.from(scope.querySelectorAll('h1, h2, h3, h4, h5, h6')).filter(
        (heading) => heading.textContent.trim() !== ''
      );
      if (!headings.length) return;

      const sublistId = `${sectionId}-sublist`;
      const sublist = document.createElement('ol');
      sublist.className = 'blog-post__toc-sublist';
      sublist.id = sublistId;
      // The list is a flex container, which drops list semantics in Safari.
      sublist.setAttribute('role', 'list');

      headings.forEach((heading, index) => {
        // Always own the id. The rich-text editor gives the merchant no way to set one, and
        // generating it unconditionally rules out colliding with an id already on the page —
        // a duplicate would make getElementById resolve two sub-links to the same heading.
        heading.id = `${sectionId}-sub-${index + 1}`;

        const listItem = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'blog-post__toc-link';
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        link.setAttribute('data-toc-link', heading.id);

        listItem.appendChild(link);
        sublist.appendChild(listItem);
      });

      const row = item.querySelector('.blog-post__toc-row');
      const parentLink = row?.querySelector('[data-toc-link]');

      // Defensive: server markup always renders the link, so this branch should not fire. If it
      // ever did, an unnamed toggle button would be worse than no toggle, so drop the caret and
      // leave the list open rather than stranding it closed with nothing to open it.
      if (row && parentLink) {
        const caret = document.createElement('button');
        caret.type = 'button';
        caret.className = 'blog-post__toc-caret';
        caret.setAttribute('aria-expanded', 'false');
        caret.setAttribute('aria-controls', sublistId);

        // Naming the control after its section keeps the announcement useful
        // without introducing a locale key for a generic "toggle" string.
        const caretLabel = document.createElement('span');
        caretLabel.className = 'sr-only';
        caretLabel.textContent = parentLink.textContent.trim();
        caret.appendChild(caretLabel);

        caret.addEventListener('click', () => {
          const expanded = item.classList.toggle('is-expanded');
          caret.setAttribute('aria-expanded', String(expanded));
        });

        row.appendChild(caret);
      } else {
        item.classList.add('is-expanded');
      }

      // The wrapper is what animates: it grids from 0fr to the list's intrinsic height.
      const subwrap = document.createElement('div');
      subwrap.className = 'blog-post__toc-subwrap';
      subwrap.appendChild(sublist);
      item.appendChild(subwrap);
    });
  }

  /**
   * Sub-heading ids only exist once buildSubnavs has run, so a `#…-sub-N` fragment in the URL
   * could not be resolved while the document was parsing. Re-apply it for our own anchors only,
   * leaving the browser's own scroll restoration alone everywhere else.
   */
  applyInitialHash() {
    const hash = window.location.hash;
    if (hash.length < 2) return;

    // No decodeURIComponent: generated ids are pure ASCII, and it throws a URIError on any lone
    // `%` in the fragment (`#50%-off`). That would escape connectedCallback before
    // observeSections() runs and silently kill scroll-spy for the whole page.
    const target = document.getElementById(hash.slice(1));
    if (target?.closest('[data-toc-scan]')) target.scrollIntoView();
  }

  /** Below this width the TOC is the collapsible nav stacked above the article (see the CSS). */
  isMobile() {
    return window.matchMedia('(max-width: 999px)').matches;
  }

  /** Reveal a sub-link that scroll-spy just activated inside a collapsed list. */
  expandFor(link) {
    // While the mobile nav is open the TOC sits above the article in normal flow, so growing it
    // here would shove the paragraph the reader is on down the page mid-scroll. Gate on the
    // viewport too: `is-open` is only ever cleared by the toggle or a link tap, so a tablet
    // rotated to landscape without either would otherwise disable auto-expand for the session.
    if (this.isMobile() && this.classList.contains('is-open')) return;
    if (!link.closest('.blog-post__toc-sublist')) return;

    const item = link.closest('[data-toc-item]');
    if (!item || item.classList.contains('is-expanded')) return;

    item.classList.add('is-expanded');
    const caret = item.querySelector('.blog-post__toc-caret');
    if (caret) caret.setAttribute('aria-expanded', 'true');
  }

  setActive(id) {
    this.links.forEach((link) => {
      const active = link.dataset.tocLink === id;
      link.classList.toggle('is-active', active);

      // Mirror onto the row so the highlight block spans the caret too. Done here rather than
      // with `:has()` in CSS — see the note on the .blog-post__toc-row rules.
      const row = link.closest('.blog-post__toc-row');
      if (row) row.classList.toggle('is-active', active);

      if (active) this.expandFor(link);
    });
  }

  observeSections() {
    const targets = this.links
      .map((link) => document.getElementById(link.dataset.tocLink))
      .filter(Boolean);

    if (!targets.length || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.filter((entry) => entry.isIntersecting);
        if (!intersecting.length) return;

        // A section box and a sub-heading inside it can enter the band together. Pick the one
        // later in document order — the sub-heading — instead of relying on notification order,
        // which the spec does not pin down.
        const winner = intersecting.reduce((current, candidate) =>
          current.target.compareDocumentPosition(candidate.target) &
          Node.DOCUMENT_POSITION_FOLLOWING
            ? candidate
            : current
        );

        this.setActive(winner.target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    targets.forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostToc);
}
